// The font sheet (unify-ui ship 4a — name-tag's, moved here so every text tool has it): language chip → mood tabs → rows,
// and "more fonts" for the whole Google catalog (catalog-view.js). Bundled rows preview the VISITOR'S text through a
// FontFace over the same file the worker shapes with (nothing leaves the site); Google rows preview the family name and
// a fixed script sample through the CSS API's `text=` parameter — never the visitor's text. The sheet owns only its own
// dialog; the page owns the document (font-control.js stands between them).
// Nothing of the core is imported (ruling R87): `core` is handed in by the tool (font-control.js lists what it holds).
import { asset } from './asset.js';   // a bundled TTF sits beside the tool's scripts, not beside the language copy of the page
import { loadGooglePreview } from './gfont.js';
import { createCatalogView } from './catalog-view.js';
import { moodTabs, curatedRows } from './picker-model.js';
import { esc } from './esc.js';

// The sheet's markup, made once a page (no template carries it): the ids the tools' browser suites drive.
const SHEET = `<div class="ui-fp-sheet">
  <div class="ui-fp-head"><h2 id="picker-title"></h2><button id="picker-close" type="button"></button></div>
  <button id="chip" type="button" class="hidden"></button>
  <div id="curated-view" class="ui-fp-view">
    <div id="moods" class="ui-seg ui-seg--wrap"></div>
    <ul id="font-rows"></ul>
  </div>
  <div id="catalog-view" class="ui-fp-view hidden">
    <div class="ui-fp-cat-head"><button id="cat-back" type="button"></button><input id="cat-search" type="search" autocomplete="off"></div>
    <div id="cat-tabs" class="ui-seg ui-seg--wrap"></div>
    <ul id="cat-rows"></ul>
  </div>
</div>`;
export function mountSheet(doc = document) {
  let dialog = doc.getElementById('picker');
  if (dialog) return dialog;
  dialog = doc.createElement('dialog');
  dialog.id = 'picker'; dialog.setAttribute('aria-labelledby', 'picker-title');
  dialog.innerHTML = SHEET;
  doc.body.append(dialog);
  return dialog;
}

export function createPicker({ dialog, curated, S, core, onPick }) {
  const $ = (s) => dialog.querySelector(s);
  const rows = $('#font-rows'), moods = $('#moods'), chip = $('#chip');
  const curatedView = $('#curated-view'), catalogRoot = $('#catalog-view');
  $('#picker-title').textContent = S.pickFont; $('#picker-close').textContent = S.close;
  let view = 'curated', currentFont = null;
  let mood = 'all', text = '', currentKey = null, cls = core.classify(''), filterOn = true;
  const failed = new Set();                                              // fontKey()s whose last load failed (the page → markFailed)

  const tabs = moodTabs(curated, core.MOODS);
  moods.innerHTML = tabs.map((m) => `<button type="button" data-v="${m}">${esc(S.mood[m])}</button>`).join('');
  const paintTabs = () => moods.querySelectorAll('button').forEach((b) => b.classList.toggle('on', b.dataset.v === mood));
  moods.addEventListener('click', (e) => { const b = e.target.closest('button'); if (!b) return; mood = b.dataset.v; paintTabs(); render(); });

  // Language chip (spec §7.1): names the scripts in the text; while on, hides fonts that cannot set it.
  // Han-only text hides nothing — CJK fonts rank first. Tapping the chip removes the filter until the next open().
  const chipTags = () => [...cls.tags, ...(cls.han ? ['han'] : [])];
  function paintChip() {
    const tags = chipTags();
    chip.classList.toggle('hidden', !tags.length || !filterOn);
    const label = tags.map((t) => S.scriptName[t]).join(' · ');
    chip.textContent = label + ' ' + S.chipMark;
    chip.setAttribute('aria-label', `${label} — ${S.chipRemove}`);          // a reader hears the language AND what the tap does
  }
  chip.addEventListener('click', () => { filterOn = false; paintChip(); render(); });

  // Lazy previews: a row's face loads the first time it scrolls into view. Bundled → FontFace over the local TTF, named
  // pv-<id> (ids are ^[a-z0-9-]+$, curated.test.js). Google → one <link> to the CSS API with the family + fixed sample as
  // `text=` (F7). Either failure keeps the fallback font; the row still picks.
  const cjkTag = (f) => f.scripts.find((s) => core.CJK_TAGS.includes(s));
  const previewOf = (f) => (f.src === 'bundled' ? (text || f.family) : `${f.family} ${S.sample[cjkTag(f)] || ''}`.trim());
  const faceOf = (f) => (f.src === 'bundled' ? `pv-${f.id}` : f.family);
  const loaded = new Set();
  const io = new IntersectionObserver((entries) => entries.forEach((en) => { if (!en.isIntersecting) return; io.unobserve(en.target); loadFace(en.target.dataset.id); }));
  function loadFace(id) {
    if (loaded.has(id)) return;
    loaded.add(id);
    const f = curated.find((x) => x.id === id);
    if (f.src === 'bundled') { const face = new FontFace(faceOf(f), `url(${asset(f.file)})`); document.fonts.add(face); face.load().catch(() => {}); return; }
    loadGooglePreview(f.family, f.variant, previewOf(f));
  }

  const moreRow = () => `<li><button type="button" id="more-fonts" class="more-row">${esc(S.moreFonts)}</button></li>`;
  function renderCurated() {
    io.disconnect();
    const list = curatedRows({ curated, mood, cls, filterOn, core });
    if (!list.length) { rows.innerHTML = `<li class="empty">${esc(filterOn && chipTags().length ? S.noFontForText : S.noFontInMood)}</li>` + moreRow(); return; }
    rows.innerHTML = list.map((f) => {
      const key = core.fontKey(f);
      const meta = [esc(f.family)];
      if (f.src === 'google') { if (cjkTag(f)) meta.push(`<span class="badge">${esc(S.scriptName[cjkTag(f)])}</span>`); meta.push(esc(S.mb(f.bytes / 1048576))); }
      if (failed.has(key)) meta.push(`<span class="badge fail">${esc(S.fontLoadFailed)}</span>`);
      return `<li><button type="button" class="font-row${key === currentKey ? ' on' : ''}${failed.has(key) ? ' failed' : ''}" data-id="${esc(f.id)}">
      <span class="pv">${esc(previewOf(f))}</span><span class="fam">${meta.join(' · ')}</span></button></li>`;
    }).join('') + moreRow();
    // font-family is assigned as a property, never interpolated into a style string: a Google id is its family name.
    rows.querySelectorAll('.font-row').forEach((b) => {
      const f = curated.find((x) => x.id === b.dataset.id);
      b.querySelector('.pv').style.fontFamily = `"${faceOf(f).replace(/["\\]/g, '')}", var(--font-body)`;
      io.observe(b);
    });
  }
  const catalogView = createCatalogView({ root: catalogRoot, S, curated, core, onPick: (f) => { onPick(f); dialog.close(); } });
  function show(v) {
    view = v;
    curatedView.classList.toggle('hidden', v !== 'curated');
    catalogRoot.classList.toggle('hidden', v !== 'catalog');
    render();
  }
  function render() {
    if (view === 'catalog') catalogView.render({ cls, filterOn, current: currentFont, failed });
    else renderCurated();
  }
  $('#cat-back').addEventListener('click', () => show('curated'));
  rows.addEventListener('click', (e) => {
    if (e.target.closest('#more-fonts')) { catalogView.reset(); show('catalog'); return; }
    const b = e.target.closest('.font-row'); if (!b) return;
    onPick(curated.find((f) => f.id === b.dataset.id));
    dialog.close();
  });
  $('#picker-close').addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });   // backdrop tap

  return {
    open(previewText, font) { text = previewText; currentKey = core.fontKey(font); cls = core.classify(text); filterOn = true; currentFont = font; paintChip(); paintTabs(); show('curated'); dialog.showModal(); rows.scrollTop = 0; },   // the list from its top at every open (M8: after a catalog pick it reopened at its end)
    markFailed(key) { failed.add(key); },
  };
}
